import { Html, Line, OrbitControls, RoundedBox } from "@react-three/drei";
import { Canvas, useFrame, useThree, type ThreeEvent } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import { DoubleSide, ExtrudeGeometry, Path, Plane, Shape, Vector3 } from "three";
import type { Group, Ray } from "three";
import type { DividerAxis, HandlePlacement, StorageDesign, WallFace } from "../domain/design";
import { getHandleAxisCenter, getHandleLength, getHandleOffset, normalizeDesign } from "../domain/design";

type SceneTool = "handle" | "divider-column" | "divider-row";
type CameraViewMode = "top" | "handle" | null;

type TraySceneProps = {
  activeTool?: SceneTool | null;
  autoRotate?: boolean;
  cameraViewMode?: CameraViewMode;
  cameraViewRevision?: number;
  design: StorageDesign;
  handleDropFace?: WallFace;
  mode?: "editor" | "hero" | "thumbnail";
  controlsEnabled?: boolean;
  interactionsEnabled?: boolean;
  onDropTool?: (tool: SceneTool, point: PointRatio) => void;
  selectedItem?: SceneSelection | null;
  onDraggingChange?: (dragging: boolean) => void;
  onMoveDivider?: (id: string, fraction: number) => void;
  onMoveHandle?: (id: string, offset: number, verticalOffset?: number) => void;
  onSelectItem?: (selection: SceneSelection | null) => void;
  onToolComplete?: () => void;
};

type BoxPiece = {
  kind?: "box";
  id: string;
  position: [number, number, number];
  radius?: number;
  scale: [number, number, number];
  tone: "shell" | "divider" | "accent" | "cut" | "handle";
  interaction?: PieceInteraction;
};

type SceneSelection =
  | { type: "divider"; id: string }
  | { type: "handle"; id: string };

type PointRatio = {
  face?: WallFace;
  vertical?: number;
  x: number;
  y: number;
};

type PieceInteraction =
  | { axis: DividerAxis; id: string; type: "divider" }
  | { face: WallFace; id: string; type: "handle" };

type ActiveDrag =
  | { axis: DividerAxis; id: string; type: "divider" }
  | { face: WallFace; id: string; type: "handle" };

type ThreePoint = [number, number, number];

type CameraView = {
  fit?: {
    direction: ThreePoint;
    margin: number;
    minDistance: number;
    radius: number;
  };
  groupRotation: ThreePoint;
  key: string;
  position: ThreePoint;
  target: ThreePoint;
};

const materialColors = {
  shell: "#4a9bdd",
  divider: "#78bde8",
  accent: "#236da8",
  cut: "#9ed1f0",
  handle: "#d8f0ff"
};

const sceneScaleByMode = {
  editor: 0.025,
  hero: 0.028,
  thumbnail: 0.03
} as const;

const wallFaceLabels: Record<WallFace, string> = {
  front: "手前面",
  right: "右面",
  back: "奥面",
  left: "左面"
};

export function TrayScene({
  activeTool = null,
  autoRotate = false,
  cameraViewMode = null,
  cameraViewRevision = 0,
  design,
  handleDropFace = "front",
  mode = "editor",
  controlsEnabled = true,
  interactionsEnabled = true,
  onDropTool,
  selectedItem = null,
  onDraggingChange,
  onMoveDivider,
  onMoveHandle,
  onSelectItem,
  onToolComplete
}: TraySceneProps) {
  const pieces = getPreviewPieces(design);
  const isEditor = mode === "editor";
  const isThumbnail = mode === "thumbnail";
  const isPresentation = mode === "hero" || isThumbnail;
  const groupRef = useRef<Group | null>(null);
  const activeDragRef = useRef<ActiveDrag | null>(null);
  const normalizedDesign = normalizeDesign(design);
  const cameraView = getCameraView(mode, activeTool, cameraViewMode, handleDropFace, normalizedDesign, cameraViewRevision);
  const sceneScale = sceneScaleByMode[mode];
  const innerWidth = Math.max(1, normalizedDesign.widthMm - normalizedDesign.wallMm * 2);
  const innerDepth = Math.max(1, normalizedDesign.depthMm - normalizedDesign.wallMm * 2);

  const intersectLocalPlane = (ray: Ray, normal: Vector3, point: Vector3) => {
    if (!groupRef.current) {
      return null;
    }

    groupRef.current.updateMatrixWorld();

    const planePoint = groupRef.current.localToWorld(point.clone());
    const planeNormal = normal.clone().transformDirection(groupRef.current.matrixWorld);
    const dragPlane = new Plane().setFromNormalAndCoplanarPoint(planeNormal, planePoint);
    const worldPoint = ray.intersectPlane(dragPlane, new Vector3());

    return worldPoint ? groupRef.current.worldToLocal(worldPoint.clone()) : null;
  };

  const moveActiveDrag = (ray: Ray) => {
    const activeDrag = activeDragRef.current;

    if (!activeDrag || !groupRef.current) {
      return;
    }

    if (activeDrag.type === "divider") {
      if (!onMoveDivider) {
        return;
      }

      const localPoint = intersectLocalPlane(ray, new Vector3(0, 1, 0), new Vector3(0, 0, 0));

      if (!localPoint) {
        return;
      }

      const fraction = activeDrag.axis === "column"
        ? (localPoint.x + innerWidth / 2) / innerWidth
        : (localPoint.z + innerDepth / 2) / innerDepth;

      onMoveDivider(activeDrag.id, fraction);
      return;
    }

    if (!onMoveHandle) {
      return;
    }

    const localPoint = intersectLocalPlane(
      ray,
      handlePlaneNormal(activeDrag.face),
      handlePlanePoint(normalizedDesign, activeDrag.face)
    );

    if (!localPoint) {
      return;
    }

    const offset = activeDrag.face === "front" || activeDrag.face === "back"
      ? (localPoint.x + normalizedDesign.widthMm / 2) / normalizedDesign.widthMm
      : (localPoint.z + normalizedDesign.depthMm / 2) / normalizedDesign.depthMm;
    const verticalOffset = normalizedDesign.handleStyle === "slot"
      ? localPoint.y / normalizedDesign.heightMm
      : undefined;

    onMoveHandle(activeDrag.id, offset, verticalOffset);
  };

  const stopDrag = () => {
    activeDragRef.current = null;
    onDraggingChange?.(false);
  };

  const dropToolFromRay = (tool: SceneTool, ray: Ray, face?: WallFace) => {
    if (!onDropTool) {
      return;
    }

    if (tool === "handle") {
      const handleFace = face ?? "right";
      const localPoint = intersectLocalPlane(
        ray,
        handlePlaneNormal(handleFace),
        handlePlanePoint(normalizedDesign, handleFace)
      );

      if (!localPoint) {
        return;
      }

      const lateralOffset = handleFace === "front" || handleFace === "back"
        ? toRatioScene((localPoint.x + normalizedDesign.widthMm / 2) / normalizedDesign.widthMm)
        : toRatioScene((localPoint.z + normalizedDesign.depthMm / 2) / normalizedDesign.depthMm);
      const verticalOffset = toRatioScene(localPoint.y / normalizedDesign.heightMm);

      onDropTool(tool, handleFace === "front" || handleFace === "back"
        ? { face: handleFace, vertical: verticalOffset, x: lateralOffset, y: handleFace === "front" ? 1 : 0 }
        : { face: handleFace, vertical: verticalOffset, x: handleFace === "right" ? 1 : 0, y: lateralOffset });
      onToolComplete?.();
      return;
    }

    const localPoint = intersectLocalPlane(ray, new Vector3(0, 1, 0), new Vector3(0, 0, 0));

    if (!localPoint) {
      return;
    }

    onDropTool(tool, {
      x: toRatioScene((localPoint.x + innerWidth / 2) / innerWidth),
      y: toRatioScene((localPoint.z + innerDepth / 2) / innerDepth)
    });
    onToolComplete?.();
  };

  return (
    <Canvas
      camera={{
        position: cameraView.position,
        fov: mode === "hero" ? 24 : isThumbnail ? 32 : 38
      }}
      gl={{ alpha: !isEditor, antialias: true }}
      shadows
    >
      {isEditor ? <color attach="background" args={["#f7fbff"]} /> : null}
      <ambientLight intensity={isPresentation ? 0.9 : 0.7} />
      <directionalLight castShadow intensity={1.45} position={[5, 7, 5]} />
      <CameraRig
        fit={cameraView.fit}
        target={cameraView.target}
        viewKey={cameraView.key}
        position={cameraView.position}
      />
      <AutoRotateRig enabled={autoRotate} groupRef={groupRef} />
      <group ref={groupRef} rotation={cameraView.groupRotation} scale={sceneScale}>
        <TrayShell design={normalizedDesign} />
        {normalizedDesign.hasBottom ? <TrayBottomPlate design={normalizedDesign} /> : null}
        {pieces.map((piece) => {
          const isSelected = piece.interaction
            ? selectedItem?.type === piece.interaction.type && selectedItem.id === piece.interaction.id
            : false;
          const isHandleHit = piece.tone === "handle";
          const interactionHandlers = {
            onClick: (event: ThreeEvent<MouseEvent>) => {
              if (!isEditor || !interactionsEnabled || !piece.interaction) {
                return;
              }

              event.stopPropagation();
              onSelectItem?.({ type: piece.interaction.type, id: piece.interaction.id });
            },
            onPointerCancel: stopDrag,
            onPointerDown: (event: ThreeEvent<PointerEvent>) => {
              if (!isEditor || !interactionsEnabled || !piece.interaction) {
                return;
              }

              event.stopPropagation();
              (event.target as { setPointerCapture?: (pointerId: number) => void } | null)?.setPointerCapture?.(event.pointerId);
              activeDragRef.current = piece.interaction;
              onDraggingChange?.(true);
              onSelectItem?.({ type: piece.interaction.type, id: piece.interaction.id });
            },
            onPointerMove: (event: ThreeEvent<PointerEvent>) => {
              if (!isEditor || !interactionsEnabled || !piece.interaction) {
                return;
              }

              event.stopPropagation();
              moveActiveDrag(event.ray);
            },
            onPointerUp: (event: ThreeEvent<PointerEvent>) => {
              if (!isEditor || !interactionsEnabled || !piece.interaction) {
                return;
              }

              event.stopPropagation();
              (event.target as { releasePointerCapture?: (pointerId: number) => void } | null)?.releasePointerCapture?.(event.pointerId);
              stopDrag();
            }
          };
          const material = (
            <meshStandardMaterial
              color={isSelected ? "#b9e4ff" : materialColors[piece.tone]}
              emissive={isSelected ? "#1d76da" : "#000000"}
              emissiveIntensity={isSelected ? 0.18 : 0}
              opacity={isHandleHit ? 0 : 1}
              transparent={isHandleHit}
              depthWrite={!isHandleHit}
              roughness={0.62}
              metalness={0.04}
            />
          );

          const radius = getRoundedBoxRadius(piece);

          if (radius > 0) {
            return (
              <RoundedBox
                key={piece.id}
                args={toThreeScale(piece.scale)}
                castShadow={!isHandleHit}
                receiveShadow={!isHandleHit}
                position={toThreePosition(piece.position)}
                radius={radius}
                smoothness={10}
                {...interactionHandlers}
              >
                {material}
              </RoundedBox>
            );
          }

          return (
            <mesh
              key={piece.id}
              castShadow={!isHandleHit}
              receiveShadow={!isHandleHit}
              position={toThreePosition(piece.position)}
              {...interactionHandlers}
            >
              <boxGeometry args={toThreeScale(piece.scale)} />
              {material}
            </mesh>
          );
        })}
        {isEditor && activeTool ? (
          <ToolDropSurface
            design={normalizedDesign}
            tool={activeTool}
            onDrop={(tool, event, face) => {
              event.stopPropagation();
              dropToolFromRay(tool, event.ray, face);
            }}
            handleFace={handleDropFace}
          />
        ) : null}
        {isEditor && activeTool === "handle" ? <HandleFaceGuide design={normalizedDesign} face={handleDropFace} /> : null}
        {isEditor ? <SceneDimensionGuides design={normalizedDesign} selection={selectedItem} /> : null}
      </group>
      {isEditor ? <gridHelper args={[8, 16, "#8fbaf0", "#d8e7f7"]} position={[0, -0.02, 0]} /> : null}
      {isEditor ? <OrbitControls enableDamping enabled={controlsEnabled} key={cameraView.key} makeDefault target={cameraView.target} /> : null}
    </Canvas>
  );
}

function toThreePosition(position: [number, number, number]): [number, number, number] {
  return [position[0], position[2], position[1]];
}

function toThreeScale(scale: [number, number, number]): [number, number, number] {
  return [scale[0], scale[2], scale[1]];
}

function CameraRig({
  fit,
  position,
  target,
  viewKey
}: {
  fit?: CameraView["fit"];
  position: ThreePoint;
  target: ThreePoint;
  viewKey: string;
}) {
  const { camera, size } = useThree();

  useEffect(() => {
    const nextPosition = fit
      ? cameraPositionFromDirection(
        target,
        fit.direction,
        Math.max(fit.minDistance, getCameraFitDistance(camera, size.width / Math.max(1, size.height), fit.radius, fit.margin))
      )
      : position;

    camera.position.set(nextPosition[0], nextPosition[1], nextPosition[2]);
    camera.lookAt(target[0], target[1], target[2]);
    camera.updateProjectionMatrix();
  }, [
    camera,
    fit?.direction[0],
    fit?.direction[1],
    fit?.direction[2],
    fit?.margin,
    fit?.minDistance,
    fit?.radius,
    position[0],
    position[1],
    position[2],
    size.height,
    size.width,
    target[0],
    target[1],
    target[2],
    viewKey
  ]);

  return null;
}

function AutoRotateRig({ enabled, groupRef }: { enabled: boolean; groupRef: { current: Group | null } }) {
  useFrame((_, delta) => {
    if (!enabled || !groupRef.current) {
      return;
    }

    groupRef.current.rotation.y += delta * 0.32;
  });

  return null;
}

function getCameraView(
  mode: TraySceneProps["mode"],
  activeTool: SceneTool | null,
  cameraViewMode: CameraViewMode,
  handleFace: WallFace,
  design: StorageDesign,
  cameraViewRevision: number
): CameraView {
  const scale = sceneScaleByMode[mode ?? "editor"];
  const scaledWidth = design.widthMm * scale;
  const scaledDepth = design.depthMm * scale;
  const scaledHeight = design.heightMm * scale;

  if (cameraViewMode === "top" || activeTool === "divider-column" || activeTool === "divider-row") {
    const direction: ThreePoint = [0, 1, 0.0001];
    const target: ThreePoint = [0, scaledHeight / 2, 0];

    return {
      fit: {
        direction,
        margin: 1.18,
        minDistance: 9,
        radius: Math.hypot(scaledWidth, scaledDepth) / 2
      },
      groupRotation: [0, 0, 0],
      key: `top-${cameraViewRevision}`,
      position: cameraPositionFromDirection(target, direction, 9),
      target
    };
  }

  if (cameraViewMode === "handle" || activeTool === "handle") {
    const directionByFace: Record<WallFace, ThreePoint> = {
      front: [0, 0.18, -1],
      right: [1, 0.18, 0],
      back: [0, 0.18, 1],
      left: [-1, 0.18, 0]
    };
    const direction = directionByFace[handleFace];
    const faceWidth = handleFace === "front" || handleFace === "back" ? scaledWidth : scaledDepth;
    const target: ThreePoint = [0, scaledHeight / 2, 0];

    return {
      fit: {
        direction,
        margin: 1.26,
        minDistance: 7.8,
        radius: Math.hypot(faceWidth, scaledHeight) / 2
      },
      groupRotation: [0, 0, 0],
      key: `handle-${handleFace}-${cameraViewRevision}`,
      position: cameraPositionFromDirection(target, direction, 7.8),
      target
    };
  }

  return {
    groupRotation: [0, -0.45, 0],
    key: mode ?? "editor",
    position: mode === "hero" ? [16, 9.8, 16] : mode === "thumbnail" ? [6.8, 5.2, 7.2] : [5.8, 4.1, 5.8],
    target: [0, 0, 0]
  };
}

function getCameraFitDistance(
  camera: unknown,
  aspect: number,
  fitRadius: number,
  margin: number
): number {
  const fov = typeof camera === "object" &&
    camera !== null &&
    "fov" in camera &&
    typeof camera.fov === "number"
    ? camera.fov
    : 38;
  const verticalHalfFov = (fov * Math.PI) / 360;
  const horizontalHalfFov = Math.atan(Math.tan(verticalHalfFov) * Math.max(0.1, aspect));
  const limitingHalfFov = Math.max(0.1, Math.min(verticalHalfFov, horizontalHalfFov));

  return Math.max(0.1, fitRadius / Math.sin(limitingHalfFov)) * margin;
}

function cameraPositionFromDirection(target: ThreePoint, direction: ThreePoint, distance: number): ThreePoint {
  const normalized = normalizeThreePoint(direction);

  return [
    target[0] + normalized[0] * distance,
    target[1] + normalized[1] * distance,
    target[2] + normalized[2] * distance
  ];
}

function normalizeThreePoint(point: ThreePoint): ThreePoint {
  const length = Math.hypot(point[0], point[1], point[2]) || 1;

  return [point[0] / length, point[1] / length, point[2] / length];
}

function ToolDropSurface({
  design,
  handleFace,
  onDrop,
  tool
}: {
  design: StorageDesign;
  handleFace: WallFace;
  onDrop: (tool: SceneTool, event: ThreeEvent<PointerEvent>, face?: WallFace) => void;
  tool: SceneTool;
}) {
  if (tool === "handle") {
    return (
      <HandleDropSurface
        design={design}
        face={handleFace}
        onDrop={(event) => onDrop(tool, event, handleFace)}
      />
    );
  }

  const innerWidth = Math.max(1, design.widthMm - design.wallMm * 2);
  const innerDepth = Math.max(1, design.depthMm - design.wallMm * 2);

  return (
    <mesh
      position={[0, design.heightMm + 0.4, 0]}
      onPointerDown={(event) => onDrop(tool, event)}
    >
      <boxGeometry args={[innerWidth, 0.4, innerDepth]} />
      <meshBasicMaterial transparent opacity={0.001} depthWrite={false} />
    </mesh>
  );
}

function HandleDropSurface({
  design,
  face,
  onDrop
}: {
  design: StorageDesign;
  face: WallFace;
  onDrop: (event: ThreeEvent<PointerEvent>) => void;
}) {
  if (face === "front" || face === "back") {
    const y = face === "front" ? -design.depthMm / 2 - 0.4 : design.depthMm / 2 + 0.4;

    return (
      <mesh position={toThreePosition([0, y, design.heightMm / 2])} onPointerDown={onDrop}>
        <boxGeometry args={[design.widthMm, design.heightMm, 1.2]} />
        <meshBasicMaterial transparent opacity={0.001} depthWrite={false} />
      </mesh>
    );
  }

  const x = face === "left" ? -design.widthMm / 2 - 0.4 : design.widthMm / 2 + 0.4;

  return (
    <mesh position={toThreePosition([x, 0, design.heightMm / 2])} onPointerDown={onDrop}>
      <boxGeometry args={[1.2, design.heightMm, design.depthMm]} />
      <meshBasicMaterial transparent opacity={0.001} depthWrite={false} />
    </mesh>
  );
}

function HandleFaceGuide({ design, face }: { design: StorageDesign; face: WallFace }) {
  const label = wallFaceLabels[face];

  if (face === "front" || face === "back") {
    const y = face === "front" ? -design.depthMm / 2 - 0.7 : design.depthMm / 2 + 0.7;

    return (
      <group>
        <mesh position={toThreePosition([0, y, design.heightMm / 2])} renderOrder={5}>
          <boxGeometry args={toThreeScale([design.widthMm, 0.6, design.heightMm])} />
          <meshBasicMaterial color="#2f8fff" transparent opacity={0.12} depthWrite={false} />
        </mesh>
        <Html center position={toThreePosition([0, y, design.heightMm + 9])} zIndexRange={[5, 0]}>
          <span className="scene-dimension-label">正面: {label}</span>
        </Html>
      </group>
    );
  }

  const x = face === "left" ? -design.widthMm / 2 - 0.7 : design.widthMm / 2 + 0.7;

  return (
    <group>
      <mesh position={toThreePosition([x, 0, design.heightMm / 2])} renderOrder={5}>
        <boxGeometry args={toThreeScale([0.6, design.depthMm, design.heightMm])} />
        <meshBasicMaterial color="#2f8fff" transparent opacity={0.12} depthWrite={false} />
      </mesh>
      <Html center position={toThreePosition([x, 0, design.heightMm + 9])} zIndexRange={[5, 0]}>
        <span className="scene-dimension-label">正面: {label}</span>
      </Html>
    </group>
  );
}

function TrayShell({ design }: { design: StorageDesign }) {
  const geometry = useMemo(() => createRoundedTrayShellGeometry(design), [
    design.cornerRadiusMm,
    design.depthMm,
    design.heightMm,
    design.wallMm,
    design.widthMm
  ]);

  return (
    <mesh castShadow receiveShadow geometry={geometry}>
      <ShellMaterial design={design} />
    </mesh>
  );
}

function ShellMaterial({ design }: { design: StorageDesign }) {
  const shaderKey = useMemo(() => getHandleCutoutShaderKey(design), [design]);
  const onBeforeCompile = useMemo(
    () => (shader: { fragmentShader: string; vertexShader: string }) => {
      const cutoutFunction = createHandleCutoutShader(design);

      shader.vertexShader = shader.vertexShader
        .replace("#include <common>", "#include <common>\nvarying vec3 vShellLocalPosition;")
        .replace("#include <begin_vertex>", "#include <begin_vertex>\nvShellLocalPosition = transformed;");
      shader.fragmentShader = shader.fragmentShader
        .replace("#include <common>", `#include <common>\nvarying vec3 vShellLocalPosition;\n${cutoutFunction}`)
        .replace("#include <clipping_planes_fragment>", "#include <clipping_planes_fragment>\nif (isHandleCutout(vShellLocalPosition)) discard;");
    },
    [design]
  );

  return (
      <meshStandardMaterial
        key={shaderKey}
        color={materialColors.shell}
        customProgramCacheKey={() => shaderKey}
        metalness={0.04}
        onBeforeCompile={onBeforeCompile}
        roughness={0.62}
        side={DoubleSide}
      />
  );
}

function getHandleCutoutShaderKey(design: StorageDesign): string {
  return [
    design.handleStyle,
    design.widthMm,
    design.depthMm,
    design.heightMm,
    design.wallMm,
    design.handleLengthMm,
    ...design.handlePlacements.map((placement) => [
      placement.id,
      placement.face,
      placement.offset,
      placement.verticalOffset ?? 0.58
    ].join(":"))
  ].join("|");
}

function createHandleCutoutShader(design: StorageDesign): string {
  if (design.handleStyle === "none" || design.handlePlacements.length === 0) {
    return "bool isHandleCutout(vec3 p) { return false; }";
  }

  const cutouts = design.handlePlacements.map((placement) => {
    const metrics = getHandleMetrics(design, placement);
    const isLongFace = placement.face === "front" || placement.face === "back";
    const faceCoord = isLongFace ? "ly" : "lx";
    const axisCoord = isLongFace ? "lx" : "ly";
    const outerPlane = placement.face === "front"
      ? -design.depthMm / 2
      : placement.face === "back"
        ? design.depthMm / 2
        : placement.face === "left"
          ? -design.widthMm / 2
          : design.widthMm / 2;
    const innerPlane = placement.face === "front"
      ? -design.depthMm / 2 + design.wallMm
      : placement.face === "back"
        ? design.depthMm / 2 - design.wallMm
        : placement.face === "left"
          ? -design.widthMm / 2 + design.wallMm
          : design.widthMm / 2 - design.wallMm;
    const minPlane = Math.min(outerPlane, innerPlane) - 0.15;
    const maxPlane = Math.max(outerPlane, innerPlane) + 0.15;
    const halfLength = metrics.length / 2;

    if (design.handleStyle === "slot") {
      const height = Math.max(2, metrics.top - metrics.bottom);
      const radius = height / 2;
      const straightHalf = Math.max(0, halfLength - radius);
      const centerZ = (metrics.bottom + metrics.top) / 2;

      return [
        `  if (${faceCoord} >= ${glslNumber(minPlane)} && ${faceCoord} <= ${glslNumber(maxPlane)}) {`,
        `    float axisDelta = abs(${axisCoord} - ${glslNumber(metrics.center)});`,
        `    float zDelta = abs(lz - ${glslNumber(centerZ)});`,
        `    float straightHalf = ${glslNumber(straightHalf)};`,
        `    float radius = ${glslNumber(radius)};`,
        "    bool inSlotCore = axisDelta <= straightHalf && zDelta <= radius;",
        "    bool inSlotCap = distance(vec2(max(axisDelta - straightHalf, 0.0), zDelta), vec2(0.0)) <= radius;",
        "    if (inSlotCore || inSlotCap) return true;",
        "  }"
      ].join("\n");
    }

    return [
      `  if (${faceCoord} >= ${glslNumber(minPlane)} && ${faceCoord} <= ${glslNumber(maxPlane)} &&`,
      `      ${axisCoord} >= ${glslNumber(metrics.center - halfLength)} && ${axisCoord} <= ${glslNumber(metrics.center + halfLength)} &&`,
      `      lz >= ${glslNumber(metrics.bottom)} && lz <= ${glslNumber(metrics.top + 0.2)}) return true;`
    ].join("\n");
  }).join("\n");

  return [
    "bool isHandleCutout(vec3 p) {",
    "  float lx = p.x;",
    "  float ly = p.z;",
    "  float lz = p.y;",
    cutouts,
    "  return false;",
    "}"
  ].join("\n");
}

function glslNumber(value: number): string {
  return Number.isFinite(value) ? value.toFixed(4) : "0.0";
}

function TrayBottomPlate({ design }: { design: StorageDesign }) {
  const geometry = useMemo(() => createRoundedPlateGeometry(design), [
    design.bottomMm,
    design.cornerRadiusMm,
    design.depthMm,
    design.widthMm
  ]);

  return (
    <mesh castShadow receiveShadow geometry={geometry}>
      <meshStandardMaterial
        color={materialColors.shell}
        metalness={0.04}
        roughness={0.62}
        side={DoubleSide}
      />
    </mesh>
  );
}

function createRoundedTrayShellGeometry(design: StorageDesign): ExtrudeGeometry {
  const shape = createRoundedRectShape(design.widthMm, design.depthMm, design.cornerRadiusMm);
  const innerWidth = Math.max(1, design.widthMm - design.wallMm * 2);
  const innerDepth = Math.max(1, design.depthMm - design.wallMm * 2);
  const innerCornerCut = Math.max(0, design.cornerRadiusMm - design.wallMm);
  shape.holes.push(createChamferedRectPath(innerWidth, innerDepth, innerCornerCut, true));

  const geometry = new ExtrudeGeometry(shape, {
    bevelEnabled: false,
    depth: design.heightMm,
    steps: 1
  });
  geometry.rotateX(-Math.PI / 2);
  geometry.computeVertexNormals();

  return geometry;
}

function createRoundedPlateGeometry(design: StorageDesign): ExtrudeGeometry {
  const shape = createRoundedRectShape(design.widthMm, design.depthMm, design.cornerRadiusMm);
  const geometry = new ExtrudeGeometry(shape, {
    bevelEnabled: false,
    depth: design.bottomMm,
    steps: 1
  });
  geometry.rotateX(-Math.PI / 2);
  geometry.computeVertexNormals();

  return geometry;
}

function createRoundedRectShape(width: number, depth: number, radius: number): Shape {
  const shape = new Shape();
  const points = createRoundedRectPath(width, depth, radius, false).getPoints(32);
  const [firstPoint, ...rest] = points;

  shape.moveTo(firstPoint.x, firstPoint.y);
  rest.forEach((point) => shape.lineTo(point.x, point.y));
  shape.closePath();

  return shape;
}

function createRoundedRectPath(width: number, depth: number, radius: number, clockwise: boolean): Path {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const r = Math.max(0, Math.min(radius, halfWidth - 0.05, halfDepth - 0.05));
  const path = new Path();

  if (r <= 0) {
    path.moveTo(-halfWidth, -halfDepth);
    if (clockwise) {
      path.lineTo(-halfWidth, halfDepth);
      path.lineTo(halfWidth, halfDepth);
      path.lineTo(halfWidth, -halfDepth);
    } else {
      path.lineTo(halfWidth, -halfDepth);
      path.lineTo(halfWidth, halfDepth);
      path.lineTo(-halfWidth, halfDepth);
    }
    path.closePath();

    return path;
  }

  if (clockwise) {
    path.moveTo(-halfWidth + r, -halfDepth);
    path.lineTo(-halfWidth, -halfDepth + r);
    path.absarc(-halfWidth + r, -halfDepth + r, r, Math.PI, -Math.PI / 2, true);
    path.lineTo(halfWidth - r, -halfDepth);
    path.absarc(halfWidth - r, -halfDepth + r, r, -Math.PI / 2, 0, true);
    path.lineTo(halfWidth, halfDepth - r);
    path.absarc(halfWidth - r, halfDepth - r, r, 0, Math.PI / 2, true);
    path.lineTo(-halfWidth + r, halfDepth);
    path.absarc(-halfWidth + r, halfDepth - r, r, Math.PI / 2, Math.PI, true);
    path.closePath();

    return path;
  }

  path.moveTo(-halfWidth + r, -halfDepth);
  path.lineTo(halfWidth - r, -halfDepth);
  path.absarc(halfWidth - r, -halfDepth + r, r, -Math.PI / 2, 0, false);
  path.lineTo(halfWidth, halfDepth - r);
  path.absarc(halfWidth - r, halfDepth - r, r, 0, Math.PI / 2, false);
  path.lineTo(-halfWidth + r, halfDepth);
  path.absarc(-halfWidth + r, halfDepth - r, r, Math.PI / 2, Math.PI, false);
  path.lineTo(-halfWidth, -halfDepth + r);
  path.absarc(-halfWidth + r, -halfDepth + r, r, Math.PI, Math.PI * 1.5, false);
  path.closePath();

  return path;
}

function createChamferedRectPath(width: number, depth: number, cornerCut: number, clockwise: boolean): Path {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const c = Math.max(0, Math.min(cornerCut, halfWidth - 0.05, halfDepth - 0.05));
  const path = new Path();

  if (c <= 0) {
    return createRoundedRectPath(width, depth, 0, clockwise);
  }

  if (clockwise) {
    path.moveTo(-halfWidth + c, -halfDepth);
    path.lineTo(-halfWidth, -halfDepth + c);
    path.lineTo(-halfWidth, halfDepth - c);
    path.lineTo(-halfWidth + c, halfDepth);
    path.lineTo(halfWidth - c, halfDepth);
    path.lineTo(halfWidth, halfDepth - c);
    path.lineTo(halfWidth, -halfDepth + c);
    path.lineTo(halfWidth - c, -halfDepth);
  } else {
    path.moveTo(-halfWidth + c, -halfDepth);
    path.lineTo(halfWidth - c, -halfDepth);
    path.lineTo(halfWidth, -halfDepth + c);
    path.lineTo(halfWidth, halfDepth - c);
    path.lineTo(halfWidth - c, halfDepth);
    path.lineTo(-halfWidth + c, halfDepth);
    path.lineTo(-halfWidth, halfDepth - c);
    path.lineTo(-halfWidth, -halfDepth + c);
  }

  path.closePath();

  return path;
}

function getRoundedBoxRadius(piece: BoxPiece): number {
  const requested = piece.radius ?? 0;

  if (requested <= 0) {
    return 0;
  }

  const smallestSide = Math.min(...piece.scale);

  return Math.max(0, Math.min(requested, smallestSide / 2 - 0.05));
}

function HandleCutoutVisuals({ design }: { design: StorageDesign }) {
  if (design.handleStyle === "none") {
    return null;
  }

  return (
    <group>
      {design.handlePlacements.map((placement) =>
        design.handleStyle === "slot" ? (
          <SlotCutoutVisual key={`${placement.id}-slot-visual`} design={design} placement={placement} />
        ) : (
          <NotchCutoutVisual key={`${placement.id}-notch-visual`} design={design} placement={placement} />
        )
      )}
    </group>
  );
}

function SlotCutoutVisual({ design, placement }: { design: StorageDesign; placement: HandlePlacement }) {
  const metrics = getHandleMetrics(design, placement);
  const slotHeight = Math.max(2, metrics.top - metrics.bottom);
  const slotShape = createCapsuleShape(metrics.length, slotHeight);
  const z = (metrics.bottom + metrics.top) / 2;
  const wallOffset = 0.08;

  if (placement.face === "front" || placement.face === "back") {
    const y = placement.face === "front" ? -design.depthMm / 2 - wallOffset : design.depthMm / 2 + wallOffset;

    return (
      <mesh position={toThreePosition([metrics.center, y, z])} renderOrder={7}>
        <shapeGeometry args={[slotShape]} />
        <meshBasicMaterial color="#f7fbff" depthWrite={false} side={DoubleSide} transparent opacity={0.96} />
      </mesh>
    );
  }

  const x = placement.face === "left" ? -design.widthMm / 2 - wallOffset : design.widthMm / 2 + wallOffset;

  return (
    <mesh position={toThreePosition([x, metrics.center, z])} renderOrder={7} rotation={[0, Math.PI / 2, 0]}>
      <shapeGeometry args={[slotShape]} />
      <meshBasicMaterial color="#f7fbff" depthWrite={false} side={DoubleSide} transparent opacity={0.96} />
    </mesh>
  );
}

function NotchCutoutVisual({ design, placement }: { design: StorageDesign; placement: HandlePlacement }) {
  const metrics = getHandleMetrics(design, placement);
  const notchHeight = Math.max(2, metrics.top - metrics.bottom);
  const notchShape = createTopNotchShape(metrics.length, notchHeight);
  const z = metrics.bottom + notchHeight / 2;
  const wallOffset = 0.1;

  if (placement.face === "front" || placement.face === "back") {
    const y = placement.face === "front" ? -design.depthMm / 2 - wallOffset : design.depthMm / 2 + wallOffset;

    return (
      <mesh position={toThreePosition([metrics.center, y, z])} renderOrder={7}>
        <shapeGeometry args={[notchShape]} />
        <meshBasicMaterial color="#f7fbff" depthWrite={false} side={DoubleSide} transparent opacity={0.96} />
      </mesh>
    );
  }

  const x = placement.face === "left" ? -design.widthMm / 2 - wallOffset : design.widthMm / 2 + wallOffset;

  return (
    <mesh position={toThreePosition([x, metrics.center, z])} renderOrder={7} rotation={[0, Math.PI / 2, 0]}>
      <shapeGeometry args={[notchShape]} />
      <meshBasicMaterial color="#f7fbff" depthWrite={false} side={DoubleSide} transparent opacity={0.96} />
    </mesh>
  );
}

function createCapsuleShape(length: number, height: number): Shape {
  const shape = new Shape();
  const radius = height / 2;
  const straightHalf = Math.max(0, length / 2 - radius);

  shape.moveTo(-straightHalf, -radius);
  shape.lineTo(straightHalf, -radius);
  shape.absarc(straightHalf, 0, radius, -Math.PI / 2, Math.PI / 2, false);
  shape.lineTo(-straightHalf, radius);
  shape.absarc(-straightHalf, 0, radius, Math.PI / 2, Math.PI * 1.5, false);
  shape.closePath();

  return shape;
}

function createTopNotchShape(length: number, height: number): Shape {
  const shape = new Shape();
  const halfLength = length / 2;
  const halfHeight = height / 2;

  shape.moveTo(-halfLength, -halfHeight);
  shape.lineTo(halfLength, -halfHeight);
  shape.lineTo(halfLength, halfHeight + 0.8);
  shape.lineTo(-halfLength, halfHeight + 0.8);
  shape.closePath();

  return shape;
}

function SceneDimensionGuides({ design, selection }: { design: StorageDesign; selection: SceneSelection | null }) {
  if (!selection) {
    return null;
  }

  if (selection.type === "divider") {
    const segment = design.dividerSegments.find((current) => current.id === selection.id);

    return segment ? <DividerDimensionGuides design={design} segment={segment} /> : null;
  }

  const placement = design.handlePlacements.find((current) => current.id === selection.id);

  return placement ? <HandleDimensionGuides design={design} placement={placement} /> : null;
}

function DividerDimensionGuides({ design, segment }: { design: StorageDesign; segment: StorageDesign["dividerSegments"][number] }) {
  const innerWidth = Math.max(1, design.widthMm - design.wallMm * 2);
  const innerDepth = Math.max(1, design.depthMm - design.wallMm * 2);
  const guideZ = (design.hasBottom ? design.bottomMm : 0) + 2.4;
  const left = -innerWidth / 2;
  const back = -innerDepth / 2;
  const dividerHalf = design.dividerMm / 2;

  if (segment.axis === "column") {
    const x = left + innerWidth * segment.fraction;
    const guideX = x + Math.max(5, design.dividerMm * 2.5);
    const spans = getDividerClearSpans({
      axisLength: innerDepth,
      dividerMm: design.dividerMm,
      selected: segment,
      crossingSegments: design.dividerSegments.filter((current) =>
        current.axis === "row" &&
        current.fraction > segment.startFraction &&
        current.fraction < segment.endFraction &&
        current.startFraction < segment.fraction &&
        current.endFraction > segment.fraction
      )
    });

    return (
      <group>
        {spans.map((span) => (
          <DimensionLine
            key={`divider-depth-dimension-${span.startFraction}-${span.endFraction}`}
            end={toThreePosition([guideX, back + span.endMm, guideZ])}
            label={`${roundMm(span.clearMm)}mm`}
            labelPosition={toThreePosition([guideX + 5, back + (span.startMm + span.endMm) / 2, guideZ + 2.4])}
            rotation={bottomTextRotation}
            start={toThreePosition([guideX, back + span.startMm, guideZ])}
            tickAxis={[1, 0, 0]}
          />
        ))}
        {spans.slice(0, -1).map((span) => {
          const crossingY = back + innerDepth * span.endFraction;

          return (
            <DimensionLine
              key={`divider-depth-thickness-${span.endFraction}`}
              end={toThreePosition([guideX, crossingY + dividerHalf, guideZ + 1.2])}
              label={`厚${roundMm(design.dividerMm)}mm`}
              labelPosition={toThreePosition([guideX + 12, crossingY, guideZ + 3.2])}
              rotation={bottomTextRotation}
              start={toThreePosition([guideX, crossingY - dividerHalf, guideZ + 1.2])}
              tickAxis={[1, 0, 0]}
            />
          );
        })}
      </group>
    );
  }

  const y = back + innerDepth * segment.fraction;
  const guideY = y + Math.max(5, design.dividerMm * 2.5);
  const spans = getDividerClearSpans({
    axisLength: innerWidth,
    dividerMm: design.dividerMm,
    selected: segment,
    crossingSegments: design.dividerSegments.filter((current) =>
      current.axis === "column" &&
      current.fraction > segment.startFraction &&
      current.fraction < segment.endFraction &&
      current.startFraction < segment.fraction &&
      current.endFraction > segment.fraction
    )
  });

  return (
    <group>
      {spans.map((span) => (
        <DimensionLine
          key={`divider-width-dimension-${span.startFraction}-${span.endFraction}`}
          end={toThreePosition([left + span.endMm, guideY, guideZ])}
          label={`${roundMm(span.clearMm)}mm`}
          labelPosition={toThreePosition([left + (span.startMm + span.endMm) / 2, guideY + 5, guideZ + 2.4])}
          rotation={bottomTextRotation}
          start={toThreePosition([left + span.startMm, guideY, guideZ])}
          tickAxis={[0, 0, 1]}
        />
      ))}
      {spans.slice(0, -1).map((span) => {
        const crossingX = left + innerWidth * span.endFraction;

        return (
          <DimensionLine
            key={`divider-width-thickness-${span.endFraction}`}
            end={toThreePosition([crossingX + dividerHalf, guideY, guideZ + 1.2])}
            label={`厚${roundMm(design.dividerMm)}mm`}
            labelPosition={toThreePosition([crossingX, guideY + 12, guideZ + 3.2])}
            rotation={bottomTextRotation}
            start={toThreePosition([crossingX - dividerHalf, guideY, guideZ + 1.2])}
            tickAxis={[0, 0, 1]}
          />
        );
      })}
    </group>
  );
}

function getDividerClearSpans({
  axisLength,
  crossingSegments,
  dividerMm,
  selected
}: {
  axisLength: number;
  crossingSegments: StorageDesign["dividerSegments"];
  dividerMm: number;
  selected: StorageDesign["dividerSegments"][number];
}) {
  const breaks = Array.from(new Set([
    selected.startFraction,
    ...crossingSegments.map((segment) => Number(clampNumber(segment.fraction, selected.startFraction, selected.endFraction).toFixed(3))),
    selected.endFraction
  ])).sort((a, b) => a - b);

  return breaks.slice(0, -1).map((startFraction, index) => {
    const endFraction = breaks[index + 1];
    const startInset = startFraction <= 0 ? 0 : dividerMm / 2;
    const endInset = endFraction >= 1 ? 0 : dividerMm / 2;
    const startMm = axisLength * startFraction + startInset;
    const endMm = axisLength * endFraction - endInset;

    return {
      clearMm: Math.max(0, endMm - startMm),
      endFraction,
      endMm,
      startFraction,
      startMm
    };
  }).filter((span) => span.clearMm > 0.2);
}

function HandleDimensionGuides({ design, placement }: { design: StorageDesign; placement: HandlePlacement }) {
  const metrics = getHandleMetrics(design, placement);
  const axisStart = -metrics.axisLength / 2;
  const axisEnd = metrics.axisLength / 2;
  const handleStart = metrics.center - metrics.length / 2;
  const handleEnd = metrics.center + metrics.length / 2;
  const outside = 1.8;
  const lineZ = clampNumber(metrics.top + 4, 5, Math.max(5, design.heightMm - 2));
  const verticalAxis = [0, 1, 0] as ThreePoint;

  if (placement.face === "front" || placement.face === "back") {
    const wallY = placement.face === "front" ? -design.depthMm / 2 - outside : design.depthMm / 2 + outside;
    const rotation = [0, 0, 0] as ThreePoint;

    return (
      <group>
        <DimensionLine
          end={toThreePosition([handleStart, wallY, lineZ])}
          label={`${roundMm(handleStart - axisStart)}mm`}
          labelPosition={toThreePosition([(axisStart + handleStart) / 2, wallY, lineZ + 5])}
          rotation={rotation}
          start={toThreePosition([axisStart, wallY, lineZ])}
          tickAxis={verticalAxis}
        />
        <DimensionLine
          end={toThreePosition([axisEnd, wallY, lineZ])}
          label={`${roundMm(axisEnd - handleEnd)}mm`}
          labelPosition={toThreePosition([(handleEnd + axisEnd) / 2, wallY, lineZ + 5])}
          rotation={rotation}
          start={toThreePosition([handleEnd, wallY, lineZ])}
          tickAxis={verticalAxis}
        />
        <HandleVerticalDimensions
          design={design}
          handleAxis={metrics.center}
          metrics={metrics}
          rotation={rotation}
          wallCoordinate={wallY}
          wallFace={placement.face}
        />
      </group>
    );
  }

  const wallX = placement.face === "left" ? -design.widthMm / 2 - outside : design.widthMm / 2 + outside;
  const rotation = [0, Math.PI / 2, 0] as ThreePoint;

  return (
    <group>
      <DimensionLine
        end={toThreePosition([wallX, handleStart, lineZ])}
        label={`${roundMm(handleStart - axisStart)}mm`}
        labelPosition={toThreePosition([wallX, (axisStart + handleStart) / 2, lineZ + 5])}
        rotation={rotation}
        start={toThreePosition([wallX, axisStart, lineZ])}
        tickAxis={verticalAxis}
      />
      <DimensionLine
        end={toThreePosition([wallX, axisEnd, lineZ])}
        label={`${roundMm(axisEnd - handleEnd)}mm`}
        labelPosition={toThreePosition([wallX, (handleEnd + axisEnd) / 2, lineZ + 5])}
        rotation={rotation}
        start={toThreePosition([wallX, handleEnd, lineZ])}
        tickAxis={verticalAxis}
      />
      <HandleVerticalDimensions
        design={design}
        handleAxis={metrics.center}
        metrics={metrics}
        rotation={rotation}
        wallCoordinate={wallX}
        wallFace={placement.face}
      />
    </group>
  );
}

function HandleVerticalDimensions({
  design,
  handleAxis,
  metrics,
  rotation,
  wallCoordinate,
  wallFace
}: {
  design: StorageDesign;
  handleAxis: number;
  metrics: ReturnType<typeof getHandleMetrics>;
  rotation: ThreePoint;
  wallCoordinate: number;
  wallFace: WallFace;
}) {
  const tickAxis = wallFace === "front" || wallFace === "back" ? [1, 0, 0] as ThreePoint : [0, 0, 1] as ThreePoint;
  const lowerStart = wallFace === "front" || wallFace === "back"
    ? toThreePosition([handleAxis, wallCoordinate, 0])
    : toThreePosition([wallCoordinate, handleAxis, 0]);
  const lowerEnd = wallFace === "front" || wallFace === "back"
    ? toThreePosition([handleAxis, wallCoordinate, metrics.bottom])
    : toThreePosition([wallCoordinate, handleAxis, metrics.bottom]);
  const upperStart = wallFace === "front" || wallFace === "back"
    ? toThreePosition([handleAxis, wallCoordinate, metrics.top])
    : toThreePosition([wallCoordinate, handleAxis, metrics.top]);
  const upperEnd = wallFace === "front" || wallFace === "back"
    ? toThreePosition([handleAxis, wallCoordinate, design.heightMm])
    : toThreePosition([wallCoordinate, handleAxis, design.heightMm]);

  return (
    <group>
      {metrics.bottom > 2 ? (
        <DimensionLine
          end={lowerEnd}
          label={`${roundMm(metrics.bottom)}mm`}
          labelPosition={midPoint(lowerStart, lowerEnd)}
          rotation={rotation}
          start={lowerStart}
          tickAxis={tickAxis}
        />
      ) : null}
      {design.heightMm - metrics.top > 2 ? (
        <DimensionLine
          end={upperEnd}
          label={`${roundMm(design.heightMm - metrics.top)}mm`}
          labelPosition={midPoint(upperStart, upperEnd)}
          rotation={rotation}
          start={upperStart}
          tickAxis={tickAxis}
        />
      ) : null}
    </group>
  );
}

function DimensionLine({
  end,
  label,
  labelPosition,
  rotation,
  start,
  tickAxis
}: {
  end: ThreePoint;
  label: string;
  labelPosition: ThreePoint;
  rotation: ThreePoint;
  start: ThreePoint;
  tickAxis: ThreePoint;
}) {
  const tick = 5;
  const startTickA = offsetPoint(start, tickAxis, -tick / 2);
  const startTickB = offsetPoint(start, tickAxis, tick / 2);
  const endTickA = offsetPoint(end, tickAxis, -tick / 2);
  const endTickB = offsetPoint(end, tickAxis, tick / 2);

  return (
    <group>
      <Line color="#2f8fff" depthTest={false} lineWidth={3} points={[start, end]} transparent opacity={0.98} renderOrder={8} />
      <Line color="#2f8fff" depthTest={false} lineWidth={3} points={[startTickA, startTickB]} transparent opacity={0.98} renderOrder={8} />
      <Line color="#2f8fff" depthTest={false} lineWidth={3} points={[endTickA, endTickB]} transparent opacity={0.98} renderOrder={8} />
      <Html center position={labelPosition} zIndexRange={[6, 0]}>
        <span className="scene-dimension-label">{label}</span>
      </Html>
    </group>
  );
}

const bottomTextRotation: ThreePoint = [-Math.PI / 2, 0, 0];

function midPoint(a: ThreePoint, b: ThreePoint): ThreePoint {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
}

function offsetPoint(point: ThreePoint, axis: ThreePoint, amount: number): ThreePoint {
  return [
    point[0] + axis[0] * amount,
    point[1] + axis[1] * amount,
    point[2] + axis[2] * amount
  ];
}

function getPreviewPieces(design: StorageDesign): BoxPiece[] {
  const d = normalizeDesign(design);
  const innerWidth = Math.max(1, d.widthMm - d.wallMm * 2);
  const innerDepth = Math.max(1, d.depthMm - d.wallMm * 2);
  const dividerHeight = d.dividerHeightMm;
  const pieces: BoxPiece[] = [...getHandleHitPieces(d)];

  d.dividerSegments.forEach((segment) => {
    if (segment.axis === "column") {
      const x = -innerWidth / 2 + innerWidth * segment.fraction;
      const segmentStart = -innerDepth / 2 + innerDepth * segment.startFraction;
      const segmentEnd = -innerDepth / 2 + innerDepth * segment.endFraction;
      const segmentDepth = Math.max(1, segmentEnd - segmentStart);
      pieces.push({
        id: segment.id,
        position: [x, segmentStart + segmentDepth / 2, (d.hasBottom ? d.bottomMm : 0) + dividerHeight / 2],
        scale: [d.dividerMm, segmentDepth, dividerHeight],
        tone: "divider",
        interaction: { type: "divider", id: segment.id, axis: segment.axis }
      });
      return;
    }

    const y = -innerDepth / 2 + innerDepth * segment.fraction;
    const segmentStart = -innerWidth / 2 + innerWidth * segment.startFraction;
    const segmentEnd = -innerWidth / 2 + innerWidth * segment.endFraction;
    const segmentWidth = Math.max(1, segmentEnd - segmentStart);
    pieces.push({
      id: segment.id,
      position: [segmentStart + segmentWidth / 2, y, (d.hasBottom ? d.bottomMm : 0) + dividerHeight / 2],
      scale: [segmentWidth, d.dividerMm, dividerHeight],
      tone: "divider",
      interaction: { type: "divider", id: segment.id, axis: segment.axis }
    });
  });

  return pieces;
}

function getWallPieces(d: StorageDesign, face: WallFace, hasHandle: boolean): BoxPiece[] {
  if (!hasHandle || d.handleStyle === "none") {
    return [createWallPiece(d, face)];
  }

  if (d.handleStyle === "slot") {
    return [createWallPiece(d, face)];
  }

  return getNotchWallPieces(d, face);
}

function getHandleHitPieces(d: StorageDesign): BoxPiece[] {
  if (d.handleStyle === "none") {
    return [];
  }

  return d.handlePlacements.map((placement) => {
    const metrics = getHandleMetrics(d, placement);
    const wallThickness = Math.max(3, d.wallMm + 1.2);

    if (placement.face === "front" || placement.face === "back") {
      const y = placement.face === "front" ? -d.depthMm / 2 : d.depthMm / 2;

      return {
        id: `${placement.id}-hit`,
        position: [metrics.center, y, (metrics.bottom + metrics.top) / 2],
        radius: d.handleStyle === "slot" ? Math.max(0, (metrics.top - metrics.bottom) / 2) : 0,
        scale: [metrics.length, wallThickness, Math.max(2, metrics.top - metrics.bottom)],
        tone: "handle",
        interaction: { type: "handle", id: placement.id, face: placement.face }
      };
    }

    const x = placement.face === "left" ? -d.widthMm / 2 : d.widthMm / 2;

    return {
      id: `${placement.id}-hit`,
      position: [x, metrics.center, (metrics.bottom + metrics.top) / 2],
      radius: d.handleStyle === "slot" ? Math.max(0, (metrics.top - metrics.bottom) / 2) : 0,
      scale: [wallThickness, metrics.length, Math.max(2, metrics.top - metrics.bottom)],
      tone: "handle",
      interaction: { type: "handle", id: placement.id, face: placement.face }
    };
  });
}

function getHandleMetrics(d: StorageDesign, placementOrFace: HandlePlacement | WallFace) {
  const face = typeof placementOrFace === "string" ? placementOrFace : placementOrFace.face;
  const axisLength = face === "front" || face === "back" ? d.widthMm : d.depthMm;
  const length = getHandleLength(axisLength, d.handleLengthMm);
  const offset = typeof placementOrFace === "string"
    ? getHandleOffset(d, face)
    : placementOrFace.offset;
  const center = getHandleAxisCenter(axisLength, length, offset);

  if (d.handleStyle === "slot") {
    const height = Math.min(14, d.heightMm * 0.38);
    const verticalOffset = typeof placementOrFace === "string"
      ? 0.58
      : placementOrFace.verticalOffset ?? 0.58;
    const centerZ = height / 2 + Math.max(0, d.heightMm - height) * clampNumber(verticalOffset, 0.08, 0.92);

    return {
      axisLength,
      bottom: Math.max(0, centerZ - height / 2),
      center,
      length,
      top: Math.min(d.heightMm, centerZ + height / 2)
    };
  }

  const height = Math.min(18, d.heightMm * 0.62);

  return {
    axisLength,
    bottom: Math.max(0, d.heightMm - height),
    center,
    length,
    top: d.heightMm
  };
}

function createWallPiece(d: StorageDesign, face: WallFace): BoxPiece {
  const wallZ = d.heightMm / 2;

  if (face === "front" || face === "back") {
    const y = face === "front" ? -d.depthMm / 2 + d.wallMm / 2 : d.depthMm / 2 - d.wallMm / 2;

    return {
      id: `${face}-wall`,
      position: [0, y, wallZ],
      scale: [d.widthMm, d.wallMm, d.heightMm],
      tone: "shell"
    };
  }

  const x = face === "left" ? -d.widthMm / 2 + d.wallMm / 2 : d.widthMm / 2 - d.wallMm / 2;

  return {
    id: `${face}-wall`,
    position: [x, 0, wallZ],
    scale: [d.wallMm, d.depthMm, d.heightMm],
    tone: "shell"
  };
}

function getNotchWallPieces(d: StorageDesign, face: WallFace): BoxPiece[] {
  const axisLength = face === "front" || face === "back" ? d.widthMm : d.depthMm;
  const notchWidth = getHandleLength(axisLength, d.handleLengthMm);
  const notchHeight = Math.min(18, d.heightMm * 0.62);
  const center = getHandleAxisCenter(axisLength, notchWidth, getHandleOffset(d, face));
  const start = Math.max(-axisLength / 2, center - notchWidth / 2);
  const end = Math.min(axisLength / 2, center + notchWidth / 2);
  const lowerHeight = Math.max(1, d.heightMm - notchHeight);
  const wallPosition = getWallAxisPosition(d, face);
  const pieces: BoxPiece[] = [];

  appendAxisWallSegment(pieces, d, face, -axisLength / 2, start, d.heightMm, d.heightMm / 2, `${face}-before`);
  appendAxisWallSegment(pieces, d, face, end, axisLength / 2, d.heightMm, d.heightMm / 2, `${face}-after`);
  appendAxisWallSegment(pieces, d, face, start, end, lowerHeight, lowerHeight / 2, `${face}-notch-lower`, wallPosition);

  return pieces;
}

function appendAxisWallSegment(
  pieces: BoxPiece[],
  d: StorageDesign,
  face: WallFace,
  start: number,
  end: number,
  height: number,
  z: number,
  id: string,
  wallPosition = getWallAxisPosition(d, face),
  radius = 0
) {
  const segmentLength = end - start;

  if (segmentLength <= 0) {
    return;
  }

  pieces.push(
    createAxisWallSegment(
      d,
      face,
      start + segmentLength / 2,
      segmentLength,
      height,
      z,
      id,
      wallPosition,
      radius
    )
  );
}

function createAxisWallSegment(
  d: StorageDesign,
  face: WallFace,
  axisCenter: number,
  axisLength: number,
  height: number,
  z: number,
  id: string,
  wallPosition = getWallAxisPosition(d, face),
  radius = 0
): BoxPiece {
  if (face === "front" || face === "back") {
    return {
      id,
      position: [axisCenter, wallPosition, z],
      radius,
      scale: [axisLength, d.wallMm, height],
      tone: "shell"
    };
  }

  return {
    id,
    position: [wallPosition, axisCenter, z],
    radius,
    scale: [d.wallMm, axisLength, height],
    tone: "shell"
  };
}

function getWallAxisPosition(d: StorageDesign, face: WallFace): number {
  switch (face) {
    case "front":
      return -d.depthMm / 2 + d.wallMm / 2;
    case "back":
      return d.depthMm / 2 - d.wallMm / 2;
    case "left":
      return -d.widthMm / 2 + d.wallMm / 2;
    case "right":
      return d.widthMm / 2 - d.wallMm / 2;
    default:
      return 0;
  }
}

function handlePlanePoint(d: StorageDesign, face: WallFace): Vector3 {
  switch (face) {
    case "front":
      return new Vector3(0, d.heightMm / 2, -d.depthMm / 2);
    case "back":
      return new Vector3(0, d.heightMm / 2, d.depthMm / 2);
    case "left":
      return new Vector3(-d.widthMm / 2, d.heightMm / 2, 0);
    case "right":
      return new Vector3(d.widthMm / 2, d.heightMm / 2, 0);
    default:
      return new Vector3(0, d.heightMm / 2, 0);
  }
}

function handlePlaneNormal(face: WallFace): Vector3 {
  switch (face) {
    case "front":
      return new Vector3(0, 0, -1);
    case "back":
      return new Vector3(0, 0, 1);
    case "left":
      return new Vector3(-1, 0, 0);
    case "right":
      return new Vector3(1, 0, 0);
    default:
      return new Vector3(0, 0, 1);
  }
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function toRatioScene(value: number): number {
  return Number(clampNumber(value, 0.18, 0.82).toFixed(3));
}

function roundMm(value: number): number {
  return Number(Math.max(0, value).toFixed(1));
}
